import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import { 
  Shield, 
  Mail, 
  Clock, 
  Ban, 
  CheckCircle, 
  Trash2, 
  Plus,
  RefreshCw,
  AlertTriangle,
  UserCheck,
  UserX,
  Warehouse,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const SecuritySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    alert_email: "",
    max_failed_attempts: 3,
    block_duration_minutes: 30,
    business_hours_start: 7,
    business_hours_end: 20,
    closed_days: [6],
    email_alerts_enabled: true,
    ip_blocking_enabled: true,
    whitelisted_ips: []
  });
  const [blockedIps, setBlockedIps] = useState([]);
  const [loginAttempts, setLoginAttempts] = useState([]);
  const [newWhitelistIp, setNewWhitelistIp] = useState("");

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [settingsRes, blockedRes, attemptsRes] = await Promise.all([
        axios.get(`${API}/auth/security/settings`),
        axios.get(`${API}/auth/security/blocked-ips`),
        axios.get(`${API}/auth/security/login-attempts?limit=20`)
      ]);
      setSettings(settingsRes.data);
      setBlockedIps(blockedRes.data.blocked_ips || []);
      setLoginAttempts(attemptsRes.data.login_attempts || []);
    } catch (error) {
      console.error("Error fetching security data:", error);
      toast.error("Failed to load security settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/auth/security/settings`, {
        alert_email: settings.alert_email,
        max_failed_attempts: settings.max_failed_attempts,
        block_duration_minutes: settings.block_duration_minutes,
        business_hours_start: settings.business_hours_start,
        business_hours_end: settings.business_hours_end,
        closed_days: settings.closed_days,
        email_alerts_enabled: settings.email_alerts_enabled,
        ip_blocking_enabled: settings.ip_blocking_enabled
      });
      toast.success("Security settings saved!");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleUnblockIp = async (ip) => {
    try {
      await axios.post(`${API}/auth/security/unblock-ip`, { ip_address: ip });
      toast.success(`IP ${ip} unblocked`);
      fetchAll();
    } catch (error) {
      toast.error("Failed to unblock IP");
    }
  };

  const handleAddWhitelist = async () => {
    if (!newWhitelistIp.trim()) {
      toast.error("Please enter an IP address");
      return;
    }
    try {
      await axios.post(`${API}/auth/security/whitelist`, { ip_address: newWhitelistIp.trim() });
      toast.success(`IP ${newWhitelistIp} added to whitelist`);
      setNewWhitelistIp("");
      fetchAll();
    } catch (error) {
      toast.error("Failed to add IP to whitelist");
    }
  };

  const handleRemoveWhitelist = async (ip) => {
    try {
      await axios.delete(`${API}/auth/security/whitelist/${encodeURIComponent(ip)}`);
      toast.success(`IP ${ip} removed from whitelist`);
      fetchAll();
    } catch (error) {
      toast.error("Failed to remove IP from whitelist");
    }
  };

  const toggleClosedDay = (day) => {
    const newClosedDays = settings.closed_days.includes(day)
      ? settings.closed_days.filter(d => d !== day)
      : [...settings.closed_days, day];
    setSettings({ ...settings, closed_days: newClosedDays });
  };

  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="security-settings-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-8 w-8 text-purple-600" />
          <div>
            <h1 className="text-2xl font-bold">Security Settings</h1>
            <p className="text-gray-500">Manage login security and alerts</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-security-btn">
          {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
          Save Settings
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Email Alerts Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Alerts
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Enable Email Alerts</Label>
              <Switch
                checked={settings.email_alerts_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, email_alerts_enabled: checked })}
              />
            </div>
            <div>
              <Label>Alert Email Address</Label>
              <Input
                value={settings.alert_email}
                onChange={(e) => setSettings({ ...settings, alert_email: e.target.value })}
                placeholder="security@yourstore.com"
                className="mt-1"
              />
            </div>
            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
              <p><strong>You'll receive alerts for:</strong></p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>3+ failed login attempts from same IP</li>
                <li>Login from new device/IP address</li>
                <li>Login outside business hours</li>
                <li>IP addresses being blocked</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* IP Blocking Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Ban className="h-5 w-5" />
              IP Blocking
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Enable IP Blocking</Label>
              <Switch
                checked={settings.ip_blocking_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, ip_blocking_enabled: checked })}
              />
            </div>
            <div>
              <Label>Block after failed attempts</Label>
              <Input
                type="number"
                value={settings.max_failed_attempts}
                onChange={(e) => setSettings({ ...settings, max_failed_attempts: parseInt(e.target.value) || 3 })}
                min={1}
                max={10}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Block duration (minutes)</Label>
              <Input
                type="number"
                value={settings.block_duration_minutes}
                onChange={(e) => setSettings({ ...settings, block_duration_minutes: parseInt(e.target.value) || 30 })}
                min={5}
                max={1440}
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Business Hours Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Business Hours (for after-hours alerts)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Opening Hour (24h)</Label>
                <Input
                  type="number"
                  value={settings.business_hours_start}
                  onChange={(e) => setSettings({ ...settings, business_hours_start: parseInt(e.target.value) || 7 })}
                  min={0}
                  max={23}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Closing Hour (24h)</Label>
                <Input
                  type="number"
                  value={settings.business_hours_end}
                  onChange={(e) => setSettings({ ...settings, business_hours_end: parseInt(e.target.value) || 20 })}
                  min={0}
                  max={23}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Closed Days</Label>
              <div className="flex gap-2 flex-wrap">
                {dayNames.map((day, index) => (
                  <Button
                    key={day}
                    variant={settings.closed_days.includes(index) ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleClosedDay(index)}
                    className={settings.closed_days.includes(index) ? "bg-red-500 hover:bg-red-600" : ""}
                  >
                    {day}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* IP Whitelist */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Whitelisted IPs (Never Blocked)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={newWhitelistIp}
                onChange={(e) => setNewWhitelistIp(e.target.value)}
                placeholder="Enter IP address (e.g., 102.164.1.228)"
                className="flex-1"
              />
              <Button onClick={handleAddWhitelist}>
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {settings.whitelisted_ips?.map((ip) => (
                <div key={ip} className="flex items-center justify-between bg-green-50 p-2 rounded">
                  <span className="font-mono text-sm">{ip}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveWhitelist(ip)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {(!settings.whitelisted_ips || settings.whitelisted_ips.length === 0) && (
                <p className="text-gray-500 text-sm">No whitelisted IPs</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Blocked IPs */}
      {blockedIps.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Currently Blocked IPs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {blockedIps.map((block) => (
                <div key={block.ip_address} className="flex items-center justify-between bg-red-50 p-3 rounded">
                  <div>
                    <span className="font-mono font-semibold">{block.ip_address}</span>
                    <p className="text-sm text-gray-600">{block.reason}</p>
                    <p className="text-xs text-gray-500">Blocked until: {new Date(block.blocked_until).toLocaleString()}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleUnblockIp(block.ip_address)}
                  >
                    Unblock
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Login Attempts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5" />
            Recent Login Attempts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Time</th>
                  <th className="text-left p-2">IP Address</th>
                  <th className="text-left p-2">Username</th>
                  <th className="text-left p-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {loginAttempts.map((attempt, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="p-2">{new Date(attempt.timestamp).toLocaleString()}</td>
                    <td className="p-2 font-mono text-xs">{attempt.ip_address}</td>
                    <td className="p-2">{attempt.username || "-"}</td>
                    <td className="p-2">
                      {attempt.success ? (
                        <span className="flex items-center gap-1 text-green-600">
                          <UserCheck className="h-4 w-4" /> Success
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-600">
                          <UserX className="h-4 w-4" /> Failed
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {loginAttempts.length === 0 && (
                  <tr>
                    <td colSpan={4} className="p-4 text-center text-gray-500">
                      No login attempts recorded
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <WarehousePinCard />
    </div>
  );
};

const WarehousePinCard = () => {
  const [status, setStatus] = useState({ has_pin: false, loading: true });
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/warehouse/status`)
      .then((r) => setStatus({ has_pin: !!r.data.has_pin, loading: false }))
      .catch(() => setStatus({ has_pin: false, loading: false }));
  }, []);

  const save = async () => {
    if (!/^\d{4,8}$/.test(pin)) {
      toast.error("PIN must be 4-8 digits");
      return;
    }
    if (pin !== confirm) {
      toast.error("PINs do not match");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/warehouse/set-pin`, { pin });
      toast.success("Warehouse PIN updated");
      setPin(""); setConfirm("");
      setStatus({ has_pin: true, loading: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save PIN");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-amber-200" data-testid="warehouse-pin-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Warehouse className="h-5 w-5 text-amber-600" />
          Warehouse Mobile PIN
        </CardTitle>
        <p className="text-sm text-gray-600">
          Shared PIN staff type on their phones to open the locked-down
          warehouse tools at <b>/mobile</b> (Bin Assign, Stock Count, Price
          Check). Employees pick their name after the PIN so every scan is
          attributed.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="text-xs">
          Status:{" "}
          <span className={status.has_pin ? "text-green-700 font-medium" : "text-red-700 font-medium"}>
            {status.loading ? "…" : (status.has_pin ? "PIN configured ✓" : "No PIN — mobile access disabled")}
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>New PIN (4-8 digits)</Label>
            <Input
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
              placeholder="••••"
              data-testid="warehouse-pin-input"
            />
          </div>
          <div>
            <Label>Confirm</Label>
            <Input
              type="password"
              inputMode="numeric"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value.replace(/\D/g, "").slice(0, 8))}
              placeholder="••••"
              data-testid="warehouse-pin-confirm"
            />
          </div>
        </div>
        <Button
          onClick={save}
          disabled={saving || !pin || !confirm}
          className="bg-amber-500 hover:bg-amber-600 text-white"
          data-testid="warehouse-pin-save"
        >
          {saving ? "Saving..." : (status.has_pin ? "Change PIN" : "Set PIN")}
        </Button>
      </CardContent>
    </Card>
  );
};

export default SecuritySettings;
